import { useState, useEffect } from 'react';
import { Modal, Input, List, Empty, Tag, Spin } from 'antd';
import { useGuardedNavigate } from '../../hooks/useGuardedNavigate';
import {
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons';

interface SearchResult {
  id: string;
  type: 'member' | 'group' | 'claim' | 'user';
  title: string;
  subtitle?: string;
  path: string;
  icon: React.ReactNode;
}

interface CommandPaletteProps {
  visible: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ visible, onClose }) => {
  const navigate = useGuardedNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Mock search function - replace with actual API calls
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      // This would be replaced with actual API calls to search endpoints
      const mockResults = [
        {
          id: '1',
          type: 'member' as const,
          title: 'John Doe',
          subtitle: 'ID: 8901234567089',
          path: '/members/1',
          icon: <UserOutlined />,
        },
        {
          id: '2',
          type: 'group' as const,
          title: 'ABC Company',
          subtitle: '45 members',
          path: '/groups/2',
          icon: <TeamOutlined />,
        },
        {
          id: '3',
          type: 'claim' as const,
          title: 'Claim #12345',
          subtitle: 'Pending - John Doe',
          path: '/claims/3',
          icon: <FileTextOutlined />,
        },
      ].filter(
        (item) =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.subtitle?.toLowerCase().includes(query.toLowerCase())
      );

      setResults(mockResults);
      setSelectedIndex(0);
      setLoading(false);
    }, 300);
  };

  useEffect(() => {
    if (visible) {
      setSearchQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [visible]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  const handleSelect = async (result: SearchResult) => {
    const didNavigate = await navigate(result.path);
    if (didNavigate) {
      onClose();
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      member: 'blue',
      group: 'green',
      claim: 'orange',
      user: 'purple',
    };
    return colors[type] || 'default';
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={null}
      width={600}
      className="command-palette-modal"
      style={{ padding: 0 }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 16,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <SearchOutlined style={{ fontSize: 20, color: '#8c8c8c', marginRight: 12 }} />
          <Input
            autoFocus
            placeholder="Search members, groups, claims... (Cmd+K)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            bordered={false}
            size="large"
            style={{ flex: 1, fontSize: 16 }}
          />
        </div>

        <div style={{ overflowY: 'auto', maxHeight: 'calc(70vh - 64px)' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin />
            </div>
          )}

          {!loading && searchQuery && results.length === 0 && (
            <Empty
              description="No results found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: '40px' }}
            />
          )}

          {!loading && results.length > 0 && (
            <List
              dataSource={results}
              renderItem={(item, index) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background-color 0.2s',
                    backgroundColor: index === selectedIndex ? '#f5f5f5' : 'transparent',
                  }}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 12 }}>
                    <div style={{ fontSize: 18, color: '#1890ff', minWidth: 24 }}>{item.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 14, color: '#262626', marginBottom: 2 }}>
                        {item.title}
                      </div>
                      {item.subtitle && (
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{item.subtitle}</div>
                      )}
                    </div>
                    <Tag color={getTypeColor(item.type)}>
                      {item.type.toUpperCase()}
                    </Tag>
                  </div>
                </List.Item>
              )}
            />
          )}

          {!loading && !searchQuery && (
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 20 }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  color: '#8c8c8c',
                  marginBottom: 12,
                  letterSpacing: 0.5,
                }}>
                  Quick Actions
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 14 }}>
                  <span>Go to Dashboard</span>
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    fontFamily: 'Monaco, Courier New, monospace',
                    fontSize: 12,
                    fontWeight: 500,
                  }}>G then D</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 14 }}>
                  <span>Go to Members</span>
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    fontFamily: 'Monaco, Courier New, monospace',
                    fontSize: 12,
                    fontWeight: 500,
                  }}>G then M</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 14 }}>
                  <span>Go to Groups</span>
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    fontFamily: 'Monaco, Courier New, monospace',
                    fontSize: 12,
                    fontWeight: 500,
                  }}>G then G</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 14 }}>
                  <span>Close this palette</span>
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    fontFamily: 'Monaco, Courier New, monospace',
                    fontSize: 12,
                    fontWeight: 500,
                  }}>ESC</span>
                </div>
              </div>
            </div>
          )}

          {!loading && searchQuery && results.length === 0 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 40,
            }}>
              <SearchOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <div style={{ fontSize: 16, color: '#8c8c8c', marginBottom: 8 }}>No results found</div>
              <div style={{ fontSize: 14, color: '#bfbfbf' }}>Try a different search term</div>
            </div>
          )}

          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 40,
            }}>
              <Spin size="large" />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CommandPalette;
